// src/pages/Training.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  School as SchoolIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningIcon,
  Quiz as QuizIcon,
  EmojiEvents as TrophyIcon,
  ExpandMore as ExpandMoreIcon,
  Badge as BadgeIcon,
  Payment as PaymentIcon,
  Lock as LockIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { trainingAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

// Shared helper functions
const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'easy': return 'success';
    case 'medium': return 'warning';
    case 'hard': return 'error';
    default: return 'default';
  }
};

const getRiskColor = (risk) => {
  switch (risk) {
    case 'high': return 'error';
    case 'moderate': return 'warning';
    case 'low': return 'success';
    default: return 'default';
  }
};

// ============================================
// CAREGIVER TRAINING (existing functionality)
// ============================================
const CaregiverTraining = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [trainingEnabled, setTrainingEnabled] = useState(true);

  // New session dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('random');
  const [sending, setSending] = useState(false);

  // History filter
  const [historyFilter, setHistoryFilter] = useState('all');

  useEffect(() => {
    loadClients();
    loadScenarios();
    trainingAPI.getStatus().then(res => setTrainingEnabled(res.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadStats();
      loadHistory();
    }
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      const response = await userAPI.getClients();
      setClients(response.data.clients || []);
      if (response.data.clients?.length > 0) {
        setSelectedClient(response.data.clients[0].id);
      }
    } catch (err) {
      setError('Failed to load people');
    } finally {
      setLoading(false);
    }
  };

  const loadScenarios = async () => {
    try {
      const response = await trainingAPI.listScenarios();
      setScenarios(response.data.scenarios || []);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await trainingAPI.getClientStats(selectedClient);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await trainingAPI.getClientHistory(selectedClient);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleSendTraining = async () => {
    setSending(true);
    setError('');

    try {
      // Create session
      const createResponse = await trainingAPI.createSession({
        client_id: selectedClient,
        scenario_id: selectedScenario === 'random' ? null : selectedScenario
      });

      // Send immediately
      await trainingAPI.sendSession(createResponse.data.session.id);

      setSuccess(`Training message sent! The scenario is "${createResponse.data.scenario.name}"`);
      setDialogOpen(false);
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send training message');
    } finally {
      setSending(false);
    }
  };

  const client = clients.find(s => s.id === selectedClient);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (clients.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No one to train</Typography>
          <Typography color="text.secondary">
            Add a loved one first to start training them to identify scams.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Training Mode
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Help your loved ones practice identifying scams with safe test messages
          </Typography>
        </Box>

        {/* Person selector */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Select Person</InputLabel>
          <Select
            value={selectedClient || ''}
            onChange={(e) => setSelectedClient(e.target.value)}
            label="Select Person"
          >
            {clients.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name || s.phone || 'Unknown'}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Alerts */}
      {!trainingEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<SchoolIcon />}>
          <Typography variant="subtitle2">Training Offline</Typography>
          <Typography variant="body2">Training exercises have been temporarily disabled by an administrator. History and stats are still available.</Typography>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 3 }, mb: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
              <QuizIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.completed}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Tests Completed</Typography>
            </CardContent>
          </Card>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
              <TrophyIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'warning.main', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.accuracy}%</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Overall Accuracy</Typography>
            </CardContent>
          </Card>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
              <WarningIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'error.main', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.byRiskLevel?.high?.accuracy || 0}%</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>High-Risk Detection</Typography>
            </CardContent>
          </Card>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
              <TimerIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'info.main', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.averageResponseTime ? `${Math.round(stats.averageResponseTime / 60)}m` : '-'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Avg Response Time</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Main Content */}
      <Paper>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Send Training" />
          <Tab label="History" />
          <Tab label="Scenarios" />
        </Tabs>

        {/* Tab 0: Send Training */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Send a Test Message</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Send {client?.name} a realistic-looking scam or legitimate message to practice identifying.
              They won't know it's a test until they respond!
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Random Scenario
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      We'll pick a random scenario they haven't seen recently.
                      Could be a scam or a legitimate message - they'll have to figure it out!
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<PlayIcon />}
                      disabled={!trainingEnabled}
                      onClick={() => {
                        setSelectedScenario('random');
                        setDialogOpen(true);
                      }}
                    >
                      Send Random Test
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Choose Specific Scenario
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Pick a specific type of scam or legitimate message to test.
                      Good for focusing on areas where they need practice.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveTab(2)}
                    >
                      Browse Scenarios
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>How it works:</strong> After sending a test message, {client?.name} should
                forward it to Second Clear like they normally would. We'll track if they correctly
                identified whether it was a scam or legitimate, and provide teaching points.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 1: History */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Training History</Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Result</InputLabel>
                <Select
                  value={historyFilter}
                  label="Result"
                  onChange={(e) => setHistoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="correct">Correct</MenuItem>
                  <MenuItem value="incorrect">Incorrect</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {history.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No training sessions yet. Send a test to get started!
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                      <TableCell>Scenario</TableCell>
                      <TableCell>Expected Risk</TableCell>
                      <TableCell>Their Answer</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Response Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.filter((session) => {
                      if (historyFilter === 'all') return true;
                      if (historyFilter === 'correct') return session.response_correct === true;
                      if (historyFilter === 'incorrect') return session.response_correct === false;
                      if (historyFilter === 'pending') return session.status === 'sent';
                      if (historyFilter === 'expired') return session.status === 'expired';
                      return true;
                    }).map((session) => (
                      <TableRow key={session.id}>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {format(new Date(session.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>{session.scenario_name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={session.expected_risk_level}
                            color={getRiskColor(session.expected_risk_level)}
                          />
                        </TableCell>
                        <TableCell>
                          {session.client_identified_as ? (
                            <Chip
                              size="small"
                              label={session.client_identified_as}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {session.status === 'sent' ? 'Awaiting response' :
                               session.status === 'expired' ? 'No response' : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {session.response_correct === true && (
                            <Chip icon={<CheckCircleIcon />} label="Correct" color="success" size="small" />
                          )}
                          {session.response_correct === false && (
                            <Chip icon={<CancelIcon />} label="Incorrect" color="error" size="small" />
                          )}
                          {session.response_correct === null && session.status === 'sent' && (
                            <Chip label="Pending" color="default" size="small" />
                          )}
                          {session.status === 'expired' && (
                            <Chip label="Expired" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {session.response_time_seconds
                            ? `${Math.round(session.response_time_seconds / 60)} min`
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 2: Scenarios */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Available Training Scenarios</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Click on a scenario to send it as a test message to {client?.name}.
            </Typography>

            <Grid container spacing={2}>
              {(() => {
                // Group scenarios by type — show one card per type
                const grouped = {};
                scenarios.forEach(s => {
                  if (!grouped[s.scenario_type]) grouped[s.scenario_type] = [];
                  grouped[s.scenario_type].push(s);
                });
                return Object.entries(grouped).map(([type, variants]) => {
                  const first = variants[0];
                  return (
                    <Grid item xs={12} sm={6} key={type}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: trainingEnabled ? 'pointer' : 'default',
                          opacity: trainingEnabled ? 1 : 0.6,
                          height: '100%',
                          '&:hover': trainingEnabled ? { borderColor: 'primary.main', bgcolor: 'grey.50' } : {}
                        }}
                        onClick={() => {
                          if (!trainingEnabled) return;
                          // Pick a random variant for this type
                          const pick = variants[Math.floor(Math.random() * variants.length)];
                          setSelectedScenario(pick.id);
                          setDialogOpen(true);
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 0.5, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {first.name}
                              {variants.length > 1 && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({variants.length} variants)
                                </Typography>
                              )}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                              <Chip
                                size="small"
                                label={first.difficulty}
                                color={getDifficultyColor(first.difficulty)}
                              />
                              <Chip
                                size="small"
                                label={first.expected_risk_level}
                                color={getRiskColor(first.expected_risk_level)}
                              />
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {first.description || first.scenario_type.replace(/_/g, ' ')}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Channel: {first.channel.toUpperCase()} |
                            Signals: {(Array.isArray(first.signals_to_identify) ? first.signals_to_identify : JSON.parse(first.signals_to_identify || '[]')).length} red flags to identify
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                });
              })()}
            </Grid>
          </Box>
        )}
      </Paper>

      <ScheduledTrainingSection />

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Training Message</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Send a test message to <strong>{client?.name}</strong>?
          </Typography>
          {selectedScenario !== 'random' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Scenario: {scenarios.find(s => s.id === selectedScenario)?.name || 'Unknown'}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            They'll receive a realistic message and should forward it to Second Clear
            like they would with a real suspicious message. This helps them practice
            identifying scams in a safe environment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendTraining}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={20} /> : <PlayIcon />}
          >
            {sending ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================
// SELF TRAINING (self-training)
// ============================================
const SelfTraining = () => {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [trainingEnabled, setTrainingEnabled] = useState(true);

  // Practice state
  const [practiceState, setPracticeState] = useState('idle'); // idle | loading | showing_scenario | answered
  const [currentSession, setCurrentSession] = useState(null);
  const [practiceResult, setPracticeResult] = useState(null);

  // Send-to-phone state
  const [sendChannel, setSendChannel] = useState('sms');
  const [sendingToPhone, setSendingToPhone] = useState(false);

  // History filter
  const [historyFilter, setHistoryFilter] = useState('all');

  useEffect(() => {
    loadData();
    trainingAPI.getStatus().then(res => setTrainingEnabled(res.data.enabled)).catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, historyRes, scenariosRes] = await Promise.all([
        trainingAPI.getMyStats(),
        trainingAPI.getMyHistory(),
        trainingAPI.listScenarios()
      ]);
      setStats(statsRes.data.stats);
      setHistory(historyRes.data.history || []);
      setScenarios(scenariosRes.data.scenarios || []);
    } catch (err) {
      console.error('Failed to load training data:', err);
      setError('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPractice = async () => {
    setPracticeState('loading');
    setError('');

    try {
      const response = await trainingAPI.startPractice();
      setCurrentSession(response.data);
      setPracticeState('showing_scenario');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start practice session');
      setPracticeState('idle');
    }
  };

  const handleAnswerPractice = async (identifiedAs) => {
    if (!currentSession) return;
    setError('');

    try {
      const response = await trainingAPI.answerPractice(currentSession.sessionId, {
        identified_as: identifiedAs
      });
      setPracticeResult(response.data.result);
      setPracticeState('answered');

      // Refresh stats and history
      const [statsRes, historyRes] = await Promise.all([
        trainingAPI.getMyStats(),
        trainingAPI.getMyHistory()
      ]);
      setStats(statsRes.data.stats);
      setHistory(historyRes.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit answer');
    }
  };

  const handleResetPractice = () => {
    setPracticeState('idle');
    setCurrentSession(null);
    setPracticeResult(null);
  };

  const handleSendToPhone = async () => {
    setSendingToPhone(true);
    setError('');

    try {
      await trainingAPI.requestTraining(sendChannel);
      setSuccess('Test message is on its way! Check your phone shortly.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test message');
    } finally {
      setSendingToPhone(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Scam Training
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Practice identifying scams and build your skills
        </Typography>
      </Box>

      {/* Alerts */}
      {!trainingEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<SchoolIcon />}>
          <Typography variant="subtitle2">Training Offline</Typography>
          <Typography variant="body2">Training exercises have been temporarily disabled. Your history and stats are still available.</Typography>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 3 }, mb: 3 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
            <QuizIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats?.completed || 0}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Tests Completed</Typography>
          </CardContent>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
            <TrophyIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'warning.main', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats?.accuracy || 0}%</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Overall Accuracy</Typography>
          </CardContent>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
            <WarningIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'error.main', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats?.byRiskLevel?.high?.accuracy || 0}%</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>High-Risk Detection</Typography>
          </CardContent>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
            <TimerIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'info.main', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats?.averageResponseTime ? `${Math.round(stats.averageResponseTime / 60)}m` : '-'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Avg Response Time</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Top 5 Things to Remember */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Top 5 Things to Remember
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List disablePadding>
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <TimerIcon sx={{ mr: 2, mt: 0.5, color: 'warning.main' }} />
              <ListItemText
                primary="Urgency & Pressure"
                secondary="Scammers create panic with deadlines ('act now', 'within 24 hours'). Real organizations give you time."
              />
            </ListItem>
            <Divider component="li" />
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <BadgeIcon sx={{ mr: 2, mt: 0.5, color: 'info.main' }} />
              <ListItemText
                primary="Authority Impersonation"
                secondary="They pretend to be the IRS, your bank, or law enforcement. Always verify by calling the real number yourself."
              />
            </ListItem>
            <Divider component="li" />
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <PaymentIcon sx={{ mr: 2, mt: 0.5, color: 'error.main' }} />
              <ListItemText
                primary="Unusual Payment Methods"
                secondary="Gift cards, wire transfers, cryptocurrency, or prepaid debit cards are almost never used by legitimate organizations."
              />
            </ListItem>
            <Divider component="li" />
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <WarningIcon sx={{ mr: 2, mt: 0.5, color: 'error.main' }} />
              <ListItemText
                primary="Threats & Consequences"
                secondary="Arrest, account closure, lawsuits — these threats are designed to scare you into acting without thinking."
              />
            </ListItem>
            <Divider component="li" />
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <LockIcon sx={{ mr: 2, mt: 0.5, color: 'warning.main' }} />
              <ListItemText
                primary="Requests for Sensitive Info"
                secondary="No legitimate organization will ask for your password, Social Security number, or bank PIN over text, email, or an unexpected call."
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            When in doubt: Hang up, don't click, don't reply. Look up the real number and call them yourself.
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Main Content */}
      <Paper>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Practice" />
          <Tab label="Send to My Phone" />
          <Tab label="History" />
        </Tabs>

        {/* Tab 0: Practice */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {practiceState === 'idle' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <QuizIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Ready to Practice?</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  We'll show you a message that could be a scam or a legitimate message.
                  Your job is to figure out which one it is!
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayIcon />}
                  onClick={handleStartPractice}
                  disabled={!trainingEnabled}
                >
                  Start Practice
                </Button>
              </Box>
            )}

            {practiceState === 'loading' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {practiceState === 'showing_scenario' && currentSession && (
              <Box>
                {/* Channel + difficulty chips */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    size="small"
                    label={currentSession.channel?.toUpperCase() || 'SMS'}
                    color="primary"
                    variant="outlined"
                  />
                  {currentSession.scenario?.difficulty && (
                    <Chip
                      size="small"
                      label={currentSession.scenario.difficulty}
                      color={getDifficultyColor(currentSession.scenario.difficulty)}
                    />
                  )}
                </Box>

                {/* Message content */}
                <Card sx={{ bgcolor: 'grey.100', mb: 3 }}>
                  <CardContent>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                    >
                      {currentSession.messageContent}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Answer prompt */}
                <Typography variant="h6" gutterBottom>
                  What do you think this message is?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={() => handleAnswerPractice('scam')}
                  >
                    Scam
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => handleAnswerPractice('legitimate')}
                  >
                    Legitimate
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleAnswerPractice('unsure')}
                  >
                    Not Sure
                  </Button>
                </Box>
              </Box>
            )}

            {practiceState === 'answered' && practiceResult && (
              <Box>
                {/* Result banner */}
                <Alert
                  severity={practiceResult.correct ? 'success' : 'warning'}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {practiceResult.correct ? 'Correct!' : 'Not quite right'}
                  </Typography>
                  <Typography variant="body2">
                    {practiceResult.correct
                      ? 'Great job identifying this message correctly!'
                      : `This message was actually ${practiceResult.expectedRisk} risk. Keep practicing!`}
                  </Typography>
                </Alert>

                {/* Answer chips */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Your Answer</Typography>
                    <Box>
                      <Chip label={practiceResult.identifiedAs} variant="outlined" />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Actual Risk Level</Typography>
                    <Box>
                      <Chip
                        label={practiceResult.expectedRisk}
                        color={getRiskColor(practiceResult.expectedRisk)}
                      />
                    </Box>
                  </Box>
                  {practiceResult.responseTimeSeconds != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Response Time</Typography>
                      <Box>
                        <Chip
                          icon={<TimerIcon />}
                          label={practiceResult.responseTimeSeconds < 60
                            ? `${practiceResult.responseTimeSeconds}s`
                            : `${Math.round(practiceResult.responseTimeSeconds / 60)}m`}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Red Flags to Notice */}
                {practiceResult.signalsToIdentify && practiceResult.signalsToIdentify.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Red Flags to Notice</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(typeof practiceResult.signalsToIdentify === 'string'
                        ? JSON.parse(practiceResult.signalsToIdentify)
                        : practiceResult.signalsToIdentify
                      ).map((signal, i) => (
                        <Chip key={i} label={signal} color="error" variant="outlined" size="small" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Teaching Points */}
                {practiceResult.teachingPoints && practiceResult.teachingPoints.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Remember</Typography>
                    <List dense>
                      {(typeof practiceResult.teachingPoints === 'string'
                        ? JSON.parse(practiceResult.teachingPoints)
                        : practiceResult.teachingPoints
                      ).map((point, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={point} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleResetPractice}
                >
                  Try Another
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: Send to My Phone */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Send Me a Test</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Receive a realistic test message on your phone or email. Respond to it like you normally
              would with Second Clear, and we'll track your results.
            </Typography>

            <FormControl sx={{ minWidth: 200, mb: 3 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={sendChannel}
                onChange={(e) => setSendChannel(e.target.value)}
                label="Channel"
              >
                <MenuItem value="sms">SMS (Text Message)</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={sendingToPhone ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSendToPhone}
                disabled={sendingToPhone || !trainingEnabled}
              >
                {sendingToPhone ? 'Sending...' : 'Send Me a Test'}
              </Button>
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>How it works:</strong> You'll receive a test message shortly after clicking the button.
                When you get it, forward it to Second Clear like you would with any suspicious message.
                We'll track whether you correctly identified it and give you feedback.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 2: History */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Training History</Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Result</InputLabel>
                <Select
                  value={historyFilter}
                  label="Result"
                  onChange={(e) => setHistoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="correct">Correct</MenuItem>
                  <MenuItem value="incorrect">Incorrect</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {history.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No training sessions yet. Start practicing to build your history!
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                      <TableCell>Scenario</TableCell>
                      <TableCell>Expected Risk</TableCell>
                      <TableCell>Your Answer</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Response Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.filter((session) => {
                      if (historyFilter === 'all') return true;
                      if (historyFilter === 'correct') return session.response_correct === true;
                      if (historyFilter === 'incorrect') return session.response_correct === false;
                      if (historyFilter === 'pending') return session.status === 'sent';
                      if (historyFilter === 'expired') return session.status === 'expired';
                      return true;
                    }).map((session) => (
                      <TableRow key={session.id}>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {format(new Date(session.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>{session.scenario_name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={session.expected_risk_level}
                            color={getRiskColor(session.expected_risk_level)}
                          />
                        </TableCell>
                        <TableCell>
                          {session.client_identified_as ? (
                            <Chip
                              size="small"
                              label={session.client_identified_as}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {session.status === 'sent' ? 'Awaiting response' :
                               session.status === 'expired' ? 'No response' : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {session.response_correct === true && (
                            <Chip icon={<CheckCircleIcon />} label="Correct" color="success" size="small" />
                          )}
                          {session.response_correct === false && (
                            <Chip icon={<CancelIcon />} label="Incorrect" color="error" size="small" />
                          )}
                          {session.response_correct === null && session.status === 'sent' && (
                            <Chip label="Pending" color="default" size="small" />
                          )}
                          {session.status === 'expired' && (
                            <Chip label="Expired" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {session.response_time_seconds
                            ? `${Math.round(session.response_time_seconds / 60)} min`
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      <ScheduledTrainingSection />
    </Box>
  );
};

// ============================================
// SHARED: Scheduled Training Settings
// ============================================
const ScheduledTrainingSection = () => {
  const [scheduledSettings, setScheduledSettings] = useState({
    training_enabled: false,
    frequency: '1x',
    timezone: 'America/New_York',
    delivery_window_start: 8,
    delivery_window_end: 20,
  });
  const [scheduledSchedule, setScheduledSchedule] = useState([]);
  const [scheduledHistory, setScheduledHistory] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(true);
  const [scheduledSaving, setScheduledSaving] = useState(false);
  const [scheduledError, setScheduledError] = useState('');
  const [scheduledSuccess, setScheduledSuccess] = useState('');

  useEffect(() => {
    loadScheduledData();
  }, []);

  const loadScheduledData = async () => {
    setScheduledLoading(true);
    try {
      const [settingsRes, scheduleRes, historyRes] = await Promise.all([
        trainingAPI.getScheduledSettings(),
        trainingAPI.getScheduledSchedule(),
        trainingAPI.getScheduledHistory(),
      ]);
      if (settingsRes.data) {
        const d = settingsRes.data;
        setScheduledSettings({
          training_enabled: d.training_enabled || false,
          frequency: d.training_frequency || '1x',
          timezone: d.training_timezone || 'America/New_York',
          delivery_window_start: d.training_window_start ?? 8,
          delivery_window_end: d.training_window_end ?? 20,
        });
      }
      setScheduledSchedule(Array.isArray(scheduleRes.data) ? scheduleRes.data : scheduleRes.data?.schedule || []);
      setScheduledHistory(Array.isArray(historyRes.data) ? historyRes.data : historyRes.data?.history || []);
    } catch (err) {
      console.error('Failed to load scheduled training data:', err);
      setScheduledError('Failed to load scheduled training data');
    } finally {
      setScheduledLoading(false);
    }
  };

  const handleSaveScheduledSettings = async () => {
    setScheduledSaving(true);
    setScheduledError('');
    setScheduledSuccess('');
    try {
      await trainingAPI.updateScheduledSettings({
        training_enabled: scheduledSettings.training_enabled,
        training_frequency: scheduledSettings.frequency,
        training_timezone: scheduledSettings.timezone,
        training_window_start: scheduledSettings.delivery_window_start,
        training_window_end: scheduledSettings.delivery_window_end,
      });
      setScheduledSuccess('Scheduled training settings saved successfully');
      const scheduleRes = await trainingAPI.getScheduledSchedule();
      setScheduledSchedule(Array.isArray(scheduleRes.data) ? scheduleRes.data : scheduleRes.data?.schedule || []);
    } catch (err) {
      setScheduledError(err.response?.data?.error || 'Failed to save scheduled training settings');
    } finally {
      setScheduledSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">Scheduled Training</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {scheduledLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {scheduledError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduledError('')}>
              {scheduledError}
            </Alert>
          )}
          {scheduledSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setScheduledSuccess('')}>
              {scheduledSuccess}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={scheduledSettings.training_enabled}
                onChange={(e) =>
                  setScheduledSettings(prev => ({ ...prev, training_enabled: e.target.checked }))
                }
                color="primary"
              />
            }
            label="Enable Scheduled Training"
            sx={{ mb: 2 }}
          />

          {scheduledSettings.training_enabled && (
            <Box sx={{ mt: 1, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={scheduledSettings.frequency}
                      label="Frequency"
                      onChange={(e) =>
                        setScheduledSettings(prev => ({ ...prev, frequency: e.target.value }))
                      }
                    >
                      <MenuItem value="1x">1x / month</MenuItem>
                      <MenuItem value="2x">2x / month</MenuItem>
                      <MenuItem value="4x">4x / month</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={scheduledSettings.timezone}
                      label="Timezone"
                      onChange={(e) =>
                        setScheduledSettings(prev => ({ ...prev, timezone: e.target.value }))
                      }
                    >
                      <MenuItem value="America/New_York">Eastern (New York)</MenuItem>
                      <MenuItem value="America/Chicago">Central (Chicago)</MenuItem>
                      <MenuItem value="America/Denver">Mountain (Denver)</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific (Los Angeles)</MenuItem>
                      <MenuItem value="America/Phoenix">Arizona (Phoenix)</MenuItem>
                      <MenuItem value="America/Anchorage">Alaska (Anchorage)</MenuItem>
                      <MenuItem value="Pacific/Honolulu">Hawaii (Honolulu)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Window Start</InputLabel>
                    <Select
                      value={scheduledSettings.delivery_window_start}
                      label="Window Start"
                      onChange={(e) =>
                        setScheduledSettings(prev => ({ ...prev, delivery_window_start: e.target.value }))
                      }
                    >
                      {Array.from({ length: 17 }, (_, i) => i + 6).map(hour => (
                        <MenuItem key={hour} value={hour}>
                          {hour <= 12 ? `${hour === 0 ? 12 : hour} AM` : `${hour - 12} PM`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Window End</InputLabel>
                    <Select
                      value={scheduledSettings.delivery_window_end}
                      label="Window End"
                      onChange={(e) =>
                        setScheduledSettings(prev => ({ ...prev, delivery_window_end: e.target.value }))
                      }
                    >
                      {Array.from({ length: 17 }, (_, i) => i + 6).map(hour => (
                        <MenuItem key={hour} value={hour}>
                          {hour <= 12 ? `${hour === 0 ? 12 : hour} AM` : `${hour - 12} PM`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveScheduledSettings}
                  disabled={scheduledSaving}
                >
                  {scheduledSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </Box>
          )}

          {scheduledSettings.training_enabled && scheduledSchedule.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Upcoming Scheduled Sends
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Scenario</TableCell>
                      <TableCell>Scheduled Date/Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduledSchedule.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.scenario_name || 'Random Scenario'}</TableCell>
                        <TableCell>
                          {item.scheduled_for
                            ? format(new Date(item.scheduled_for), 'MMM d, yyyy h:mm a')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {scheduledSettings.training_enabled && scheduledSchedule.length === 0 && !scheduledLoading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No upcoming scheduled sends. Save your settings to generate a schedule.
              </Typography>
            </Box>
          )}

          {scheduledHistory.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Recent Send History
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Scenario</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduledHistory.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.scenario_name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.status}
                            color={
                              item.status === 'sent' || item.status === 'delivered'
                                ? 'success'
                                : item.status === 'failed'
                                ? 'error'
                                : item.status === 'pending'
                                ? 'warning'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {item.sent_at
                            ? format(new Date(item.sent_at), 'MMM d, yyyy h:mm a')
                            : item.created_at
                            ? format(new Date(item.created_at), 'MMM d, yyyy h:mm a')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

// ============================================
// MAIN COMPONENT - Role-based routing
// ============================================
const Training = () => {
  const { user } = useAuth();
  const isCaregiver = ['owner', 'caregiver', 'self'].includes(user?.accountType);

  if (isCaregiver) return <CaregiverTraining />;
  return <SelfTraining />;
};

export default Training;
